import { useState, type Dispatch, type SetStateAction } from 'react';
import { Download } from 'lucide-react';
import { CTCBreakdown } from './CTCBreakdown';
import { InfoPanel } from './InfoPanel';
import { ptRates } from '../data/ptRates';
import type { SalaryStructure, TaxDeductions, PTState, PTSlab, EPFWageMode } from '../types';
import { downloadAsExcel, downloadAsPDF } from '../utils/exportFormats';
import { ChevronDown } from 'lucide-react';
import { HelpCircle } from 'lucide-react';
import { Tooltip } from './Tooltip';
import { SalaryStructureInputs } from './SalaryStructureInputs';
import { calculateSalaryBenefits, type SalaryBenefitsInput } from '../utils/salaryBenefits';
import { SalaryBenefitsInputs } from './SalaryBenefitsInputs';

const EPF_WAGE_CEILING = 15000;

export function Calculator() {
  const [ctc, setCTC] = useState<string>('');
  const [frequency, setFrequency] = useState<'monthly' | 'yearly'>('yearly');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedState, setSelectedState] = useState<PTState>(ptRates[0]);
  const [customPT, setCustomPT] = useState<string>('200');
  const [isPTApplicable, setIsPTApplicable] = useState(true);
  const [isEPFApplicable, setIsEPFApplicable] = useState(true);
  const [epfWageMode, setEpfWageMode] = useState<EPFWageMode>('capped');
  const [epfEmployeeRate, setEpfEmployeeRate] = useState('12');
  const [epfEmployerRate, setEpfEmployerRate] = useState('12');
  const [epfCustomMonthlyWage, setEpfCustomMonthlyWage] = useState('15000');
  const [structure, setStructure] = useState<Record<keyof SalaryStructure, string>>({
    basic: '40',
    hra: '20',
    da: '10',
    lta: '5',
    specialAllowance: '15',
    performanceBonus: '10',
    epf: '12',
    professionalTax: '2.5',
    esi: '1.75',
    incomeTax: '10',
  });
  const [taxRegime, setTaxRegime] = useState<'new' | 'old'>('new');
  const [deductions, setDeductions] = useState<Record<keyof TaxDeductions, string>>({
    section80C: '',
    section80D: '',
    section80TTA: '',
    homeLoanInterest: '',
    rentPaid: '',
    otherDeductions: '',
    nps: '',
  });
  const [showTaxDetails, setShowTaxDetails] = useState(false);
  const [showMobileBreakdown, setShowMobileBreakdown] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [structureValues, setStructureValues] = useState<Record<keyof SalaryStructure, string>>({
    basic: '',
    hra: '',
    da: '',
    lta: '',
    specialAllowance: '',
    performanceBonus: '',
    epf: '',
    professionalTax: '',
    esi: '',
    incomeTax: '',
  });
  const [isMetroCity, setIsMetroCity] = useState(true);
  const [isSelfSenior, setIsSelfSenior] = useState(false);
  const [hasParents, setHasParents] = useState(false);
  const [isParentsSenior, setIsParentsSenior] = useState(false);
  const [isRentedProperty, setIsRentedProperty] = useState(false);
  const [structureMode, setStructureMode] = useState<'percentage' | 'value'>('percentage');
  const [benefits, setBenefits] = useState<SalaryBenefitsInput>({
    enabledNps: false, employeeNps: 0, employerNps: 0, governmentEmployer: false,
    enabledMeals: false, mealCoupons: 0, qualifyingMeals: false, workingDays: 22, mealsPerDay: 1,
  });

  const sanitizeDecimalInput = (value: string) => {
    const numericValue = value.replace(/[^0-9.]/g, '');
    const [whole, ...decimalParts] = numericValue.split('.');
    const decimal = decimalParts.join('');
    const normalized = decimalParts.length > 0 ? `${whole}.${decimal}` : whole;

    return /^\d{0,3}(\.\d{0,2})?$/.test(normalized) ? normalized : null;
  };

  const handleEpfRateChange = (
    setter: Dispatch<SetStateAction<string>>,
    value: string
  ) => {
    const numericValue = sanitizeDecimalInput(value);
    if (numericValue !== null) setter(numericValue);
  };

  const getEpfWageBase = (basic: number, da: number, monthlyGrossSalary: number) => {
    const basicDaWages = basic + da;

    switch (epfWageMode) {
      case 'basicDa':
        return basicDaWages;
      case 'labourCode':
        return Math.max(basicDaWages, monthlyGrossSalary * 0.5);
      case 'custom':
        return Number(epfCustomMonthlyWage) || 0;
      case 'capped':
      default:
        return Math.min(basicDaWages, EPF_WAGE_CEILING);
    }
  };

  const formatIndianNumber = (value: string): string => {
    const number = value.replace(/[^0-9]/g, '');
    if (number === '') return '';
    
    const lastThree = number.substring(number.length - 3);
    const otherNumbers = number.substring(0, number.length - 3);
    const formatted = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
    
    return otherNumbers ? formatted + ',' + lastThree : lastThree;
  };

  const handleCTCChange = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    if (frequency === 'monthly') {
      setCTC(numericValue === '' ? '' : String(Number(numericValue) * 12));
    } else {
      setCTC(numericValue);
    }
  };

  const getDisplayCTC = () => {
    if (ctc === '') return '';
    const numericValue = frequency === 'monthly' ? 
      Math.round(Number(ctc) / 12).toString() : 
      ctc;
    return formatIndianNumber(numericValue);
  };

  const handleStructureChange = (key: keyof SalaryStructure | 'mode', value: string) => {
    if (key === 'mode') {
      setStructureMode(value as 'percentage' | 'value');
      if (value === 'value' && ctc) {
        // Convert current percentages to values
        const baseValue = frequency === 'monthly' ? Number(ctc) / 12 : Number(ctc);
        const newValues = Object.entries(structure).reduce((acc, [k, v]) => ({
          ...acc,
          [k]: Math.round((Number(v) * baseValue) / 100).toString()
        }), {} as Record<keyof SalaryStructure, string>);
        setStructureValues(newValues);
      }
      return;
    }

    if (structureMode === 'percentage') {
      // Percentage mode logic remains the same
      if (value !== '' && !/^\d+$/.test(value)) return;
      
      const newStructure = {
        ...structure,
        [key]: value
      };
      
      const totalPercentage = Object.entries(newStructure)
        .filter(([k]) => ['basic', 'hra', 'da', 'lta', 'specialAllowance', 'performanceBonus'].includes(k))
        .reduce((sum, [, value]) => sum + (Number(value) || 0), 0);
      
      if (totalPercentage !== 100) {
        setValidationError(`Total percentage should be 100%. Current total: ${totalPercentage}%`);
      } else {
        setValidationError(null);
      }
      
      setStructure(newStructure);
    } else {
      // Value mode - direct input
      if (value !== '' && !/^\d+$/.test(value)) return;
      
      const newValues = {
        ...structureValues,
        [key]: value
      };
      
      const baseValue = frequency === 'monthly' ? Number(ctc) / 12 : Number(ctc);
      const totalValue = Object.entries(newValues)
        .filter(([k]) => ['basic', 'hra', 'da', 'lta', 'specialAllowance', 'performanceBonus'].includes(k))
        .reduce((sum, [, val]) => sum + (Number(val) || 0), 0);
      
      if (totalValue > baseValue) {
        setValidationError(`Total amount cannot exceed ${frequency === 'monthly' ? 'monthly' : 'yearly'} CTC of ${
          new Intl.NumberFormat('en-IN').format(baseValue)
        }`);
        return;
      }
      
      setValidationError(null);
      setStructureValues(newValues);

      // Convert amounts back to percentages for calculation
      const newPercentages = {
        ...structure
      };
      Object.entries(newValues).forEach(([k, v]) => {
        const percentage = ((Number(v) || 0) / baseValue) * 100;
        newPercentages[k as keyof SalaryStructure] = percentage.toString();
      });
      setStructure(newPercentages);
    }
  };

  const validate80CDeduction = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const amount = Number(numericValue);
    const limit = 150000; // ₹1.5 lakh limit

    if (amount > limit) {
      alert(`Section 80C deductions cannot exceed ₹1,50,000`);
      return String(limit);
    }
    return numericValue;
  };

  const validate80DDeduction = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const amount = Number(numericValue);
    
    // Calculate total limit based on selected options
    let totalLimit = 25000; // Base limit for self & family
    
    if (isSelfSenior) {
      totalLimit = 50000;
    }
    
    if (hasParents) {
      totalLimit += isParentsSenior ? 50000 : 25000;
    }

    if (amount > totalLimit) {
      alert(`Section 80D deductions cannot exceed ₹${totalLimit.toLocaleString()}`);
      return String(totalLimit);
    }
    return numericValue;
  };

  const validate80TTADeduction = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const amount = Number(numericValue);
    const limit = 10000; // ₹10,000 limit for savings account interest

    if (amount > limit) {
      alert(`Section 80TTA deductions cannot exceed ₹10,000`);
      return String(limit);
    }
    return numericValue;
  };

  const validateHomeLoanInterest = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const amount = Number(numericValue);
    const limit = 200000; // ₹2L limit for self-occupied property

    if (amount > limit) {
      alert(`Home Loan Interest deduction cannot exceed ₹2,00,000 for self-occupied property`);
      return String(limit);
    }
    return numericValue;
  };

  const validateNPSDeduction = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const amount = Number(numericValue);
    const limit = 50000; // ₹50,000 limit for NPS under 80CCD(1B)

    if (amount > limit) {
      alert(`NPS deduction under Section 80CCD(1B) cannot exceed ₹50,000`);
      return String(limit);
    }
    return numericValue;
  };

  const handleDeductionChange = (key: keyof TaxDeductions, value: string) => {
    let numericValue = value.replace(/[^0-9]/g, '');
    
    switch(key) {
      case 'section80C':
        numericValue = validate80CDeduction(value);
        break;
      case 'section80D':
        numericValue = validate80DDeduction(value);
        break;
      case 'section80TTA':
        numericValue = validate80TTADeduction(value);
        break;
      case 'homeLoanInterest':
        numericValue = validateHomeLoanInterest(value);
        break;
      case 'nps':
        numericValue = validateNPSDeduction(value);
        break;
    }

    setDeductions(prev => ({
      ...prev,
      [key]: numericValue
    }));
  };

  const calculateHRAExemption = (basicSalary: number, hraReceived: number, rentPaid: number, isMetroCity: boolean = true) => {
    const actualRentPaid = Number(rentPaid);
    const percentageOfBasic = isMetroCity ? 0.5 * basicSalary : 0.4 * basicSalary;
    const rentMinusBasic = actualRentPaid - (0.1 * basicSalary);

    return Math.min(
      hraReceived,
      percentageOfBasic,
      rentMinusBasic > 0 ? rentMinusBasic : 0
    );
  };

  const calculateTaxBreakdown = (annualIncome: number, components?: { basic: number; hra: number }, employeeNps = 0) => {
    let taxableIncome = annualIncome;
    
    // Apply standard deduction based on tax regime
    const standardDeduction = taxRegime === 'new' ? 75000 : 50000;
    taxableIncome -= standardDeduction;
    
    if (taxRegime === 'old' && components) {
      const hraExemption = calculateHRAExemption(
        components.basic * 12, // Convert to annual
        components.hra * 12,   // Convert to annual
        Number(deductions.rentPaid),
        isMetroCity
      );
      
      taxableIncome -= hraExemption;
      
      taxableIncome -= (
        Number(deductions.section80C) +
        Number(deductions.section80D) +
        Number(deductions.section80TTA) +
        Number(deductions.homeLoanInterest) +
        Math.min(50000, Number(deductions.nps) + employeeNps) +
        Math.min(Number(deductions.rentPaid) * 0.1, 60000) +
        Number(deductions.otherDeductions)
      );
    }

    let baseTax = 0;
    let surcharge = 0;
    let marginalRelief = 0;
    let rebate = 0;

    // Calculate base tax based on regime
    if (taxRegime === 'new') {
      // New tax regime slabs retained for Tax Year 2026-27 under the Income-tax Act, 2025.
      if (taxableIncome <= 400000) {
        baseTax = 0;
      } else if (taxableIncome <= 800000) {
        baseTax = (taxableIncome - 400000) * 0.05;
      } else if (taxableIncome <= 1200000) {
        baseTax = 20000 + (taxableIncome - 800000) * 0.10;
      } else if (taxableIncome <= 1600000) {
        baseTax = 60000 + (taxableIncome - 1200000) * 0.15;
      } else if (taxableIncome <= 2000000) {
        baseTax = 120000 + (taxableIncome - 1600000) * 0.20;
      } else if (taxableIncome <= 2400000) {
        baseTax = 200000 + (taxableIncome - 2000000) * 0.25;
      } else {
        baseTax = 300000 + (taxableIncome - 2400000) * 0.30;
      }

      // Apply rebate for taxable income up to 12L.
      if (taxableIncome <= 1200000) {
        rebate = Math.min(baseTax, 60000);
        baseTax = Math.max(0, baseTax - rebate);
      }
      // Apply Section 87A marginal relief where tax exceeds income above 12L.
      else {
        const excessIncome = taxableIncome - 1200000;
        marginalRelief = Math.max(0, baseTax - excessIncome);
      }
    } else {
      if (taxableIncome <= 250000) {
        baseTax = 0;
      } else if (taxableIncome <= 500000) {
        baseTax = (taxableIncome - 250000) * 0.05;
      } else if (taxableIncome <= 1000000) {
        baseTax = 12500 + (taxableIncome - 500000) * 0.2;
      } else {
        baseTax = 112500 + (taxableIncome - 1000000) * 0.3;
      }
    }

    // Calculate surcharge based on taxable income
    if (taxableIncome > 50000000) { // > 5 crore
      surcharge = baseTax * (taxRegime === 'new' ? 0.25 : 0.37);
    } else if (taxableIncome > 20000000) { // > 2 crore
      surcharge = baseTax * 0.25;
    } else if (taxableIncome > 10000000) { // > 1 crore
      surcharge = baseTax * 0.15;
    } else if (taxableIncome > 5000000) { // > 50 lakh
      surcharge = baseTax * 0.10;
    }

    // Calculate marginal relief on surcharge thresholds.
    if (surcharge > 0) {
      const thresholds = [5000000, 10000000, 20000000, 50000000];
      const applicableThreshold = [...thresholds].reverse().find(t => taxableIncome > t);
      
      if (applicableThreshold) {
        const excessIncome = taxableIncome - applicableThreshold;
        const taxWithSurcharge = baseTax + surcharge;
        const thresholdBaseTax = calculateBaseTax(applicableThreshold);
        const thresholdSurcharge = calculateSurchargeForIncome(applicableThreshold, thresholdBaseTax);
        const taxAtThreshold = thresholdBaseTax + thresholdSurcharge;
        const additionalTax = taxWithSurcharge - taxAtThreshold;
        
        if (additionalTax > excessIncome) {
          marginalRelief = additionalTax - excessIncome;
        }
      }
    }

    const taxBeforeCess = Math.max(0, baseTax + surcharge - marginalRelief);
    const cess = taxBeforeCess * 0.04;

    return {
      standardDeduction,
      base: baseTax,
      surcharge,
      marginalRelief,
      rebate,
      cess,
      total: taxBeforeCess + cess
    };
  };

  // Helper function to calculate base tax without surcharge
  const calculateBaseTax = (income: number): number => {
    let tax = 0;
    if (taxRegime === 'new') {
      // New tax regime slabs retained for Tax Year 2026-27 under the Income-tax Act, 2025.
      if (income <= 400000) {
        tax = 0;
      } else if (income <= 800000) {
        tax = (income - 400000) * 0.05;
      } else if (income <= 1200000) {
        tax = 20000 + (income - 800000) * 0.10;
      } else if (income <= 1600000) {
        tax = 60000 + (income - 1200000) * 0.15;
      } else if (income <= 2000000) {
        tax = 120000 + (income - 1600000) * 0.20;
      } else if (income <= 2400000) {
        tax = 200000 + (income - 2000000) * 0.25;
      } else {
        tax = 300000 + (income - 2400000) * 0.30;
      }
    } else {
      if (income <= 250000) {
        tax = 0;
      } else if (income <= 500000) {
        tax = (income - 250000) * 0.05;
      } else if (income <= 1000000) {
        tax = 12500 + (income - 500000) * 0.2;
      } else {
        tax = 112500 + (income - 1000000) * 0.3;
      }
    }
    return tax;
  };

  const calculateSurchargeForIncome = (income: number, tax: number): number => {
    if (income > 50000000) return tax * (taxRegime === 'new' ? 0.25 : 0.37);
    if (income > 20000000) return tax * 0.25;
    if (income > 10000000) return tax * 0.15;
    if (income > 5000000) return tax * 0.10;
    return 0;
  };

  const calculatePT = (monthlyIncome: number, state: PTState): number => {
    if (!isPTApplicable) return 0;
    if (state.id === 'custom') return Number(customPT);

    const applicableSlab = state.slabs.find(
      (slab: PTSlab) => monthlyIncome >= slab.min && (slab.max === null || monthlyIncome <= slab.max)
    );

    if (!applicableSlab) return 0;

    // Calculate professional tax based on the applicable slab
    const professionalTax = applicableSlab.rate;

    // Cap the professional tax at ₹2,500 annually for all states
    return Math.min(professionalTax, 2500);
  };

  const calculateSalaryComponents = () => {
    if (!ctc) return null;

    // Always work with yearly CTC first, then convert as needed
    const yearlyCtc = Number(ctc);
    const monthlyCtc = yearlyCtc / 12;
    const monthly = frequency === 'monthly';

    // Calculate monthly components
    const basic = (monthlyCtc * Number(structure.basic)) / 100;
    const hra = (monthlyCtc * Number(structure.hra)) / 100;
    const da = (monthlyCtc * Number(structure.da)) / 100;
    const lta = (monthlyCtc * Number(structure.lta)) / 100;
    const special = (monthlyCtc * Number(structure.specialAllowance)) / 100;
    const bonus = (monthlyCtc * Number(structure.performanceBonus)) / 100;

    const monthlyGrossSalary = basic + hra + da + lta + special + bonus;
    const yearlyGrossSalary = monthlyGrossSalary * 12;
    const salaryBenefits = calculateSalaryBenefits(benefits, special, basic + da, taxRegime);

    // Calculate yearly tax first
    const yearlyTax = calculateTaxBreakdown(yearlyGrossSalary - 12 * (
      salaryBenefits.employerDeduction + salaryBenefits.mealExemption
    ), {
      basic: basic,
      hra: hra
    }, salaryBenefits.employee * 12);
    
    // Convert to monthly tax
    const monthlyTax = {
      standardDeduction: yearlyTax.standardDeduction / 12,
      base: yearlyTax.base / 12,
      surcharge: yearlyTax.surcharge / 12,
      marginalRelief: yearlyTax.marginalRelief / 12,
      rebate: yearlyTax.rebate / 12,
      cess: yearlyTax.cess / 12,
      total: yearlyTax.total / 12
    };

    // Monthly deductions
    const epfWageBase = isEPFApplicable ? getEpfWageBase(basic, da, monthlyGrossSalary) : 0;
    const epfEmployee = isEPFApplicable ? (epfWageBase * Number(epfEmployeeRate || 0)) / 100 : 0;
    const epfEmployer = isEPFApplicable ? (epfWageBase * Number(epfEmployerRate || 0)) / 100 : 0;

    const esiApplicable = (basic + da) <= 21000;
    const esiEmployee = esiApplicable ? (monthlyGrossSalary * Number(structure.esi)) / 100 : 0;
    const esiEmployer = esiApplicable ? (monthlyGrossSalary * 3.25) / 100 : 0;

    const monthlyPT = calculatePT(monthlyGrossSalary, selectedState);
    const yearlyPT = monthlyPT * 12;
    const pt = monthly ? monthlyPT : yearlyPT;

    // Calculate total deductions
    const monthlyDeductions = epfEmployee + esiEmployee + monthlyPT + monthlyTax.total
      + salaryBenefits.employee + salaryBenefits.employer + salaryBenefits.meals;
    const yearlyDeductions = monthlyDeductions * 12;

    // Calculate net salary
    const monthlyNetSalary = monthlyGrossSalary - monthlyDeductions;
    const yearlyNetSalary = yearlyGrossSalary - yearlyDeductions;

    return {
      basic: monthly ? basic : basic * 12,
      hra: monthly ? hra : hra * 12,
      da: monthly ? da : da * 12,
      lta: monthly ? lta : lta * 12,
      special: monthly ? salaryBenefits.specialAllowance : salaryBenefits.specialAllowance * 12,
      bonus: monthly ? bonus : bonus * 12,
      grossSalary: monthly ? monthlyGrossSalary : yearlyGrossSalary,
      epf: {
        employee: monthly ? epfEmployee : epfEmployee * 12,
        employer: monthly ? epfEmployer : epfEmployer * 12,
        total: monthly ? epfEmployee : epfEmployee * 12,
        applicable: isEPFApplicable,
        wageBase: monthly ? epfWageBase : epfWageBase * 12,
        wageMode: epfWageMode,
        employeeRate: Number(epfEmployeeRate || 0),
        employerRate: Number(epfEmployerRate || 0)
      },
      esi: {
        employee: monthly ? esiEmployee : esiEmployee * 12,
        employer: monthly ? esiEmployer : esiEmployer * 12,
        total: monthly ? esiEmployee : esiEmployee * 12,
        applicable: esiApplicable
      },
      pt: pt,
      tax: {
        standardDeduction: monthly ? monthlyTax.standardDeduction : yearlyTax.standardDeduction,
        base: monthly ? monthlyTax.base : yearlyTax.base,
        surcharge: monthly ? monthlyTax.surcharge : yearlyTax.surcharge,
        marginalRelief: monthly ? monthlyTax.marginalRelief : yearlyTax.marginalRelief,
        rebate: monthly ? monthlyTax.rebate : yearlyTax.rebate,
        cess: monthly ? monthlyTax.cess : yearlyTax.cess,
        total: monthly ? monthlyTax.total : yearlyTax.total
      },
      totalDeductions: monthly ? monthlyDeductions : yearlyDeductions,
      netSalary: monthly ? monthlyNetSalary : yearlyNetSalary,
      benefits: {
        employeeNps: salaryBenefits.employee * (monthly ? 1 : 12),
        employerNps: salaryBenefits.employer * (monthly ? 1 : 12),
        employerNpsDeduction: salaryBenefits.employerDeduction * (monthly ? 1 : 12),
        mealCoupons: salaryBenefits.meals * (monthly ? 1 : 12),
        mealExemption: salaryBenefits.mealExemption * (monthly ? 1 : 12),
      },
    };
  };

  const handleDownload = (format: 'pdf' | 'excel') => {
    const calculation = calculateSalaryComponents();
    if (calculation) {
      const adjustedCalculation = {
        ...calculation,
        tax: {
          ...calculation.tax,
          standardDeduction: calculation.tax.standardDeduction ? calculation.tax.standardDeduction : 0 // Ensure standardDeduction is always present
        }
      };
      if (format === 'pdf') {
        downloadAsPDF(adjustedCalculation, frequency, Number(ctc), showTaxDetails);
      } else {
        downloadAsExcel(adjustedCalculation, frequency, Number(ctc), showTaxDetails);
      }
    }
  };

  const calculation = calculateSalaryComponents();

  return (
    <div className="space-y-8">
      <div className="bg-white rounded-xl shadow-lg p-4 md:p-8">
        <div className={`grid gap-6 md:gap-8 ${showAdvanced ? 'lg:grid-cols-[1.5fr,1fr]' : 'md:grid-cols-2'}`}>
          {/* Left Column - Input Section */}
          <div className="space-y-6">
            <div className="space-y-4">
              {/* CTC Input and Frequency Toggle */}
              <label className="block">
                <span className="text-gray-700 text-lg font-medium">Cost to Company (CTC)</span>
                <div className="mt-2 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-lg">₹</span>
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={getDisplayCTC()}
                    onChange={(e) => handleCTCChange(e.target.value)}
                    className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-8 pr-12 py-3 text-lg border-gray-300 rounded-md"
                    placeholder={`Enter your ${frequency} CTC`}
                    aria-label={`Enter your ${frequency} Cost to Company`}
                    aria-describedby="ctc-description"
                  />
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                    <span className="text-gray-500 text-sm">{frequency}</span>
                  </div>
                </div>
                <p id="ctc-description" className="mt-1 text-sm text-gray-500">
                  Enter your {frequency} Cost to Company (CTC) amount in Indian Rupees
                </p>
              </label>

              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setFrequency('monthly');
                    if (ctc) {
                      setCTC(String(Number(ctc)));
                    }
                  }}
                  className={`flex-1 py-2 px-4 rounded-md ${
                    frequency === 'monthly'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Monthly
                </button>
                <button
                  onClick={() => {
                    setFrequency('yearly');
                    if (ctc) {
                      setCTC(String(Number(ctc)));
                    }
                  }}
                  className={`flex-1 py-2 px-4 rounded-md ${
                    frequency === 'yearly'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Yearly
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-700 font-medium">Advanced Settings</span>
                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                >
                  {showAdvanced ? 'Hide' : 'Show'}
                  <ChevronDown className={`w-4 h-4 transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                </button>
              </div>
            </div>

            {/* Advanced Settings Section */}
            {showAdvanced && (
              <div className="space-y-6 lg:max-h-[calc(100vh-24rem)] lg:overflow-y-auto lg:pr-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-medium text-gray-900">Customize Components</h3>
                </div>
                {validationError && (
                  <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                    {validationError}
                  </div>
                )}

                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-1">
                      <span className="text-sm font-medium text-gray-700">Component</span>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="text-sm font-medium text-gray-700">Percentage</span>
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="text-sm font-medium text-gray-700">Amount</span>
                    </div>
                  </div>

                  <SalaryStructureInputs
                    mode={structureMode}
                    structure={structure}
                    structureValues={structureValues}
                    onStructureChange={handleStructureChange}
                    validationError={validationError}
                    ctc={ctc}
                  />
                </div>

                {/* Tax Regime Selection */}
                <div className="flex items-center space-x-4 mb-4">
                  <span className="text-sm text-gray-600">Tax Regime:</span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setTaxRegime('old')}
                      className={`px-3 py-1 text-sm rounded-md ${
                        taxRegime === 'old'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Old
                    </button>
                    <button
                      onClick={() => setTaxRegime('new')}
                      className={`px-3 py-1 text-sm rounded-md ${
                        taxRegime === 'new'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      New
                    </button>
                  </div>
                </div>

                {/* EPF and PT Options */}
                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="epf"
                      checked={isEPFApplicable}
                      onChange={(e) => setIsEPFApplicable(e.target.checked)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="epf" className="text-sm text-gray-600">
                      EPF Applicable
                    </label>
                  </div>

                  {isEPFApplicable && (
                    <div className="ml-6 space-y-4 rounded-md border border-gray-200 bg-gray-50 p-4">
                      <label className="block">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">PF Wage Basis</span>
                          <Tooltip content="EPF is commonly calculated on Basic + DA. Employers may cap the PF wage at Rs 15,000 per month, use actual Basic + DA, or model the labour-code wage floor.">
                            <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                          </Tooltip>
                        </div>
                        <select
                          value={epfWageMode}
                          onChange={(e) => setEpfWageMode(e.target.value as EPFWageMode)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="capped">Capped at Rs 15,000 wage</option>
                          <option value="basicDa">Actual Basic + DA</option>
                          <option value="labourCode">Labour-code wage floor</option>
                          <option value="custom">Custom monthly PF wage</option>
                        </select>
                      </label>

                      {epfWageMode === 'custom' && (
                        <label className="block">
                          <span className="text-sm text-gray-600">Monthly PF Wage</span>
                          <div className="mt-1 relative rounded-md shadow-sm">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500 sm:text-sm">₹</span>
                            </div>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={epfCustomMonthlyWage}
                              onChange={(e) => setEpfCustomMonthlyWage(e.target.value.replace(/[^0-9]/g, ''))}
                              className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                              placeholder="15000"
                            />
                          </div>
                        </label>
                      )}

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <label className="block">
                          <span className="text-sm text-gray-600">Employee EPF %</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={epfEmployeeRate}
                            onChange={(e) => handleEpfRateChange(setEpfEmployeeRate, e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="12"
                          />
                        </label>
                        <label className="block">
                          <span className="text-sm text-gray-600">Employer EPF %</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={epfEmployerRate}
                            onChange={(e) => handleEpfRateChange(setEpfEmployerRate, e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="12"
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="pt"
                      checked={isPTApplicable}
                      onChange={(e) => setIsPTApplicable(e.target.checked)}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="pt" className="text-sm text-gray-600">
                      Professional Tax Applicable
                    </label>
                  </div>

                  {isPTApplicable && (
                    <div className="ml-6 space-y-2">
                      <select
                        value={selectedState.id}
                        onChange={(e) => {
                          const state = ptRates.find(s => s.id === e.target.value);
                          if (state) setSelectedState(state);
                        }}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        {ptRates.map(state => (
                          <option key={state.id} value={state.id}>
                            {state.name}
                          </option>
                        ))}
                      </select>

                      {selectedState.id === 'custom' && (
                        <input
                          type="text"
                          value={customPT}
                          onChange={(e) => setCustomPT(e.target.value)}
                          placeholder="Enter custom PT amount"
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      )}
                    </div>
                  )}
                </div>

                <SalaryBenefitsInputs
                  value={benefits}
                  onChange={setBenefits}
                  taxRegime={taxRegime}
                  monthlySpecialAllowance={Number(ctc) / 12 * Number(structure.specialAllowance) / 100}
                  monthlyBasicDa={Number(ctc) / 12 * (Number(structure.basic) + Number(structure.da)) / 100}
                />

                {taxRegime === 'old' && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-gray-900">Tax Deductions (Old Regime)</h4>
                    
                    {/* Metro City Toggle for HRA */}
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="metroCity"
                        checked={isMetroCity}
                        onChange={(e) => setIsMetroCity(e.target.checked)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <label htmlFor="metroCity" className="text-sm text-gray-600">
                        Metro City (for HRA calculation)
                      </label>
                      <Tooltip content="Select if you live in a metro city (Mumbai, Delhi, Chennai, Kolkata) for HRA calculation">
                        <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                      </Tooltip>
                    </div>

                    {/* Rent Paid Input */}
                    <div className="mt-4">
                      <label className="block">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-600">Monthly Rent Paid</span>
                          <Tooltip content="Enter your monthly rent amount for HRA exemption calculation">
                            <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                          </Tooltip>
                        </div>
                        <div className="mt-1 relative rounded-md shadow-sm">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 sm:text-sm">₹</span>
                          </div>
                          <input
                            type="text"
                            value={deductions.rentPaid}
                            onChange={(e) => handleDeductionChange('rentPaid', e.target.value)}
                            className="mt-1 block w-full pl-7 pr-12 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Enter monthly rent"
                          />
                        </div>
                      </label>
                    </div>

                    {/* Section 80C */}
                    <label className="block">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Section 80C (Max ₹1.5L)</span>
                        <Tooltip content="Includes EPF, PPF, ELSS, Life Insurance Premium, Children's Tuition Fees, Principal repayment of Home Loan, etc.">
                          <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                        </Tooltip>
                      </div>
                      <input
                        type="text"
                        value={deductions.section80C}
                        onChange={(e) => handleDeductionChange('section80C', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Enter amount"
                      />
                    </label>

                    {/* Section 80CCD(1B) - NPS */}
                    <label className="block">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Personal NPS outside payroll (annual)</span>
                        <Tooltip content="Additional personal Tier I NPS deduction: up to ₹50,000 annually combined with payroll NPS under Section 124(3), formerly 80CCD(1B). Do not enter payroll contributions again here. This field reduces tax only, not salary credited to your bank.">
                          <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                        </Tooltip>
                      </div>
                      <input
                        type="text"
                        value={deductions.nps}
                        onChange={(e) => handleDeductionChange('nps', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Enter amount"
                      />
                    </label>

                    {/* Section 80D */}
                    <div className="space-y-2">
                      <label className="block">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-sm text-gray-600">Section 80D (Health Insurance)</span>
                          <Tooltip content="Medical insurance premium for self, family and parents. Limits vary based on age.">
                            <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                          </Tooltip>
                        </div>
                        <input
                          type="text"
                          value={deductions.section80D}
                          onChange={(e) => handleDeductionChange('section80D', e.target.value)}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="Enter amount"
                        />
                      </label>
                      <div className="mt-3 space-y-3 ml-4">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="selfSenior"
                            checked={isSelfSenior}
                            onChange={(e) => setIsSelfSenior(e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="selfSenior" className="text-sm text-gray-600">
                            Self/Family are Senior Citizens (60+ years)
                          </label>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="hasParents"
                            checked={hasParents}
                            onChange={(e) => setHasParents(e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="hasParents" className="text-sm text-gray-600">
                            Include Parents
                          </label>
                        </div>
                        
                        {hasParents && (
                          <div className="flex items-center space-x-2 ml-4">
                            <input
                              type="checkbox"
                              id="parentsSenior"
                              checked={isParentsSenior}
                              onChange={(e) => setIsParentsSenior(e.target.checked)}
                              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <label htmlFor="parentsSenior" className="text-sm text-gray-600">
                              Parents are Senior Citizens (60+ years)
                            </label>
                            <Tooltip content="Higher deduction limit (₹50,000) applies if parents are senior citizens">
                              <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                            </Tooltip>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Section 80TTA */}
                    <label className="block">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Section 80TTA (Savings Interest)</span>
                        <Tooltip content="Deduction for interest earned from savings account, maximum ₹10,000">
                          <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                        </Tooltip>
                      </div>
                      <input
                        type="text"
                        value={deductions.section80TTA}
                        onChange={(e) => handleDeductionChange('section80TTA', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Enter amount"
                      />
                    </label>

                    {/* Home Loan Interest */}
                    <label className="block">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">Home Loan Interest</span>
                        <Tooltip content="Interest paid on home loan. Maximum ₹2L for self-occupied property. No limit for rented property.">
                          <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
                        </Tooltip>
                      </div>
                      <input
                        type="text"
                        value={deductions.homeLoanInterest}
                        onChange={(e) => handleDeductionChange('homeLoanInterest', e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Enter amount"
                      />
                      <div className="mt-2 space-y-2 ml-4">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="isRentedProperty"
                            checked={isRentedProperty}
                            onChange={(e) => setIsRentedProperty(e.target.checked)}
                            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <label htmlFor="isRentedProperty" className="text-sm text-gray-600">
                            Property is rented out
                          </label>
                        </div>
                      </div>
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Salary Breakdown */}
          <div className={`${showAdvanced ? 'hidden lg:block' : ''}`}>
            <h2 className="sr-only">Salary Calculator Results</h2>
            {calculation && (
              <>
                <CTCBreakdown
                  calculation={calculation}
                  frequency={frequency}
                  showTaxDetails={showTaxDetails}
                  setShowTaxDetails={setShowTaxDetails}
                  taxRegime={taxRegime}
                />
              </>
            )}
          </div>

          {/* Mobile Salary Breakdown (shown when advanced settings are open) */}
          {showAdvanced && calculation && (
            <div className="lg:hidden">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Salary Details</h2>
                <button
                  onClick={() => setShowMobileBreakdown(!showMobileBreakdown)}
                  className="text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                >
                  {showMobileBreakdown ? 'Hide' : 'Show'}
                  <ChevronDown className={`w-4 h-4 transform transition-transform ${showMobileBreakdown ? 'rotate-180' : ''}`} />
                </button>
              </div>
              {showMobileBreakdown && (
                <CTCBreakdown
                  calculation={calculation}
                  frequency={frequency}
                  showTaxDetails={showTaxDetails}
                  setShowTaxDetails={setShowTaxDetails}
                  taxRegime={taxRegime}
                />
              )}
            </div>
          )}
        </div>

        <div className="mt-8 flex justify-end space-x-4">
          <div className="flex space-x-2">
            <button
              onClick={() => handleDownload('pdf')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </button>
            <button
              onClick={() => handleDownload('excel')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium 
              rounded-md shadow-sm text-white bg-emerald-700 hover:bg-emerald-800 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-600"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Excel
            </button>
          </div>
        </div>
      </div>

      <InfoPanel />
    </div>
  );
}
